/**
 * QuestionComparison
 *
 * Specialized component for comparing questions between before/after.
 * Displays each question in an accordion with change indicators.
 */

import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { Question } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface QuestionComparisonProps {
  beforeQuestions: Question[];
  afterQuestions: Question[];
}

type ChangeType = 'unchanged' | 'modified' | 'added' | 'removed';

interface QuestionDiff {
  index: number;
  changeType: ChangeType;
  before?: Question;
  after?: Question;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Check if two questions are different
 */
function questionsAreDifferent(q1: Question, q2: Question): boolean {
  if (q1.texte !== q2.texte) return true;
  if (q1.type !== q2.type) return true;
  if (q1.reponseCorrecte !== q2.reponseCorrecte) return true;
  if (q1.explication !== q2.explication) return true;
  if (q1.points !== q2.points) return true;

  // Compare options arrays
  const opts1 = q1.options || [];
  const opts2 = q2.options || [];
  if (opts1.length !== opts2.length) return true;
  if (opts1.some((opt, i) => opt !== opts2[i])) return true;

  return false;
}

/**
 * Build diff between before and after questions
 */
function buildQuestionsDiff(
  before: Question[],
  after: Question[]
): QuestionDiff[] {
  const diffs: QuestionDiff[] = [];
  const maxLength = Math.max(before.length, after.length);

  for (let i = 0; i < maxLength; i++) {
    const beforeQ = before[i];
    const afterQ = after[i];

    if (!beforeQ && afterQ) {
      // Question added
      diffs.push({ index: i, changeType: 'added', after: afterQ });
    } else if (beforeQ && !afterQ) {
      // Question removed
      diffs.push({ index: i, changeType: 'removed', before: beforeQ });
    } else if (beforeQ && afterQ) {
      // Both exist - check if modified
      const isModified = questionsAreDifferent(beforeQ, afterQ);
      diffs.push({
        index: i,
        changeType: isModified ? 'modified' : 'unchanged',
        before: beforeQ,
        after: afterQ
      });
    }
  }

  return diffs;
}

/**
 * Get change indicator color and icon
 */
function getChangeIndicator(changeType: ChangeType) {
  switch (changeType) {
    case 'modified':
      return { color: 'warning' as const, icon: <EditIcon fontSize="small" />, label: 'Modifiee' };
    case 'added':
      return { color: 'success' as const, icon: <AddIcon fontSize="small" />, label: 'Ajoutee' };
    case 'removed':
      return { color: 'error' as const, icon: <RemoveIcon fontSize="small" />, label: 'Supprimee' };
    default:
      return null;
  }
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface QuestionFieldProps {
  label: string;
  before?: string | number | string[];
  after?: string | number | string[];
}

const QuestionField: React.FC<QuestionFieldProps> = ({ label, before, after }) => {
  const beforeStr = Array.isArray(before) ? before.join(', ') : String(before ?? '');
  const afterStr = Array.isArray(after) ? after.join(', ') : String(after ?? '');
  const isChanged = beforeStr !== afterStr;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight="medium">
        {label} {isChanged && <Chip label="modifie" size="small" color="warning" sx={{ ml: 1, height: 18 }} />}
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={6}>
          <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {beforeStr || '(vide)'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              bgcolor: isChanged ? 'warning.lighter' : 'grey.50',
              borderColor: isChanged ? 'warning.main' : 'divider'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontWeight: isChanged ? 'medium' : 'normal'
              }}
            >
              {afterStr || '(vide)'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const QuestionComparison: React.FC<QuestionComparisonProps> = ({
  beforeQuestions,
  afterQuestions
}) => {
  const diffs = buildQuestionsDiff(beforeQuestions, afterQuestions);

  const addedCount = diffs.filter(d => d.changeType === 'added').length;
  const removedCount = diffs.filter(d => d.changeType === 'removed').length;
  const modifiedCount = diffs.filter(d => d.changeType === 'modified').length;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flexGrow: 1 }}>
          QUESTIONS ({beforeQuestions.length} → {afterQuestions.length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {modifiedCount > 0 && (
            <Chip
              icon={<EditIcon fontSize="small" />}
              label={`${modifiedCount} modifiee${modifiedCount > 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {addedCount > 0 && (
            <Chip
              icon={<AddIcon fontSize="small" />}
              label={`${addedCount} ajoutee${addedCount > 1 ? 's' : ''}`}
              size="small"
              color="success"
              variant="outlined"
            />
          )}
          {removedCount > 0 && (
            <Chip
              icon={<RemoveIcon fontSize="small" />}
              label={`${removedCount} supprimee${removedCount > 1 ? 's' : ''}`}
              size="small"
              color="error"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      {diffs.map((diff) => {
        const indicator = getChangeIndicator(diff.changeType);
        const question = diff.after || diff.before;

        return (
          <Accordion
            key={diff.index}
            defaultExpanded={diff.changeType !== 'unchanged'}
            sx={{
              mb: 1,
              '&:before': { display: 'none' },
              borderLeft: indicator ? `3px solid` : 'none',
              borderLeftColor: indicator ? `${indicator.color}.main` : 'transparent'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1 }}>
                  Question {diff.index + 1}: {question?.texte.substring(0, 50)}...
                </Typography>
                {indicator && (
                  <Chip
                    icon={indicator.icon}
                    label={indicator.label}
                    size="small"
                    color={indicator.color}
                    sx={{ mr: 1 }}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {diff.changeType === 'removed' ? (
                <Typography color="error" variant="body2">
                  Cette question sera supprimee.
                </Typography>
              ) : diff.changeType === 'added' ? (
                <Box>
                  <Typography color="success.main" variant="body2" sx={{ mb: 2 }}>
                    Nouvelle question
                  </Typography>
                  <Typography variant="body2"><strong>Texte:</strong> {diff.after?.texte}</Typography>
                  <Typography variant="body2"><strong>Type:</strong> {diff.after?.type}</Typography>
                  <Typography variant="body2"><strong>Reponse:</strong> {diff.after?.reponseCorrecte}</Typography>
                  <Typography variant="body2"><strong>Points:</strong> {diff.after?.points}</Typography>
                  {diff.after?.options && (
                    <Typography variant="body2"><strong>Options:</strong> {diff.after.options.join(', ')}</Typography>
                  )}
                </Box>
              ) : (
                <Box>
                  <QuestionField label="Texte" before={diff.before?.texte} after={diff.after?.texte} />
                  <Divider sx={{ my: 1.5 }} />
                  <QuestionField label="Type" before={diff.before?.type} after={diff.after?.type} />
                  <Divider sx={{ my: 1.5 }} />
                  <QuestionField label="Reponse correcte" before={diff.before?.reponseCorrecte} after={diff.after?.reponseCorrecte} />
                  <Divider sx={{ my: 1.5 }} />
                  <QuestionField label="Explication" before={diff.before?.explication} after={diff.after?.explication} />
                  <Divider sx={{ my: 1.5 }} />
                  <QuestionField label="Points" before={diff.before?.points} after={diff.after?.points} />
                  {(diff.before?.options || diff.after?.options) && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <QuestionField label="Options" before={diff.before?.options} after={diff.after?.options} />
                    </>
                  )}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Paper>
  );
};

export default QuestionComparison;
